import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { Row, Col, Input, Select, InputNumber, Button } from 'antd';
import { StopOutlined } from '@ant-design/icons';

import { Form, WrapperSelect } from 'common/components';
import { useGetNutritionEnum } from '../hook/useNutritionLabel';
import { useFactPanelQasRequiredFields } from '../hook/useFactPanelQasRequiredFields';

import { sortBy } from 'lodash';
import { NUTRIENTS_DATA } from '../constants';

import { QaSpecHelpContentWithoutLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';

const { Option } = Select;

const NutrientForm = (props) => {
  const {
    isEdit = true,
    productId,
    typeView,
    method,
    form,
    dv,
    onCancel,
  } = props;

  const refAddBtn = useRef();

  const { measurementPrecisionCodeEnum, dailyPrecisionCodeEnum } =
    useGetNutritionEnum();

  const filterSelectedDV = useMemo(() => {
    const dvName = dv?.map((dv) => dv.nutritionTypeCode);

    if (!NUTRIENTS_DATA) return [];

    return sortBy(
      NUTRIENTS_DATA.filter(
        (nutrition) => !dvName.includes(nutrition.enumValue)
      ),
      ['enumDisplayName', 'enumGroup']
    );
  }, [dv, NUTRIENTS_DATA]);

  const isQaSpecType = typeView === 'qa-spec';

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.nutritionLabel.nutrients',
  });

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const styles = {
    wrapper: {
      margin: '15px 0',
    },
    inputNumber: {
      width: '100%',
    },

    selectAfter: {
      width: '72px',
    },
    unit: {
      padding: '5px 8px',
      marginLeft: '5px',
      background: '#DCDCDC',
      borderRadius: '20px',
    },
    form: {
      marginBottom: 10,
    },
    formItem: {
      marginBottom: '4px',
    },
    dv: {
      textAlign: 'center',
    },
    rowMargin: {
      marginBottom: 5,
    },
    cancelBtn: {
      width: '100%',
      height: 35,
    },
  };

  useEffect(() => {
    refAddBtn.current && refAddBtn.current.focus();
  }, [form]);

  return (
    <div style={styles.wrapper}>
      {isEdit && (
        <React.Fragment>
          <Form
            form={form}
            layout='vertical'
            name='dvForm'
            initialValues={{
              quantityUom: 'Gram',
              measurementPrecisionCode: 'Exact', // Set default value to "Exact",
              dailyValuePrecisionCode: 'Exact', // Set default value to "Exact",
            }}
            style={styles.form}
          >
            <Form.Item
              name='nutritionTypeCode'
              rules={[{ required: true, message: 'Please select nutrient' }]}
              style={styles.formItem}
            >
              <QaSpecHelpContentWithoutLabel
                fieldPath={TOOlTIP_FIELDS.nutritionTypeCode.fieldPath}
                isEnabled={isQaSpecType}
              >
                <WrapperSelect
                  placeholder='Select'
                  showSearch
                  filterOption={(input, option) =>
                    option?.title?.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                  }
                  getPopupContainer={(triggerNode) => {
                    return triggerNode;
                  }}
                  optionFilterProp='children'
                >
                  {filterSelectedDV?.map((item, idx) => (
                    <Option
                      key={idx}
                      value={item?.enumValue}
                      title={item?.enumDisplayName}
                    >
                      {item?.enumDisplayName}
                    </Option>
                  ))}
                </WrapperSelect>
              </QaSpecHelpContentWithoutLabel>
            </Form.Item>
            <Form.Item
              name='measurementPrecisionCode'
              style={styles.formItem}
              rules={[
                getRequiredRule({
                  fieldName: ['measurementPrecisionCode'],
                  displayName: 'Measurement Precision',
                }),
              ]}
            >
              <QaSpecHelpContentWithoutLabel
                fieldPath={TOOlTIP_FIELDS.measurementPrecisionCode.fieldPath}
                isEnabled={isQaSpecType}
              >
                <WrapperSelect
                  placeholder='Select Measurement Precision'
                  showSearch
                  filterOption={(input, option) =>
                    option?.value
                      ?.toLowerCase()
                      .indexOf(input?.toLowerCase()) >= 0
                  }
                  getPopupContainer={(triggerNode) => {
                    return triggerNode;
                  }}
                >
                  {sortBy(measurementPrecisionCodeEnum?.[0]?.enumProperties, [
                    'enumDisplayName',
                    'enumGroup',
                  ])?.map((item, index) => (
                    <Option value={item?.enumDisplayName} key={index}>
                      {item?.enumDisplayName}
                    </Option>
                  ))}
                </WrapperSelect>
              </QaSpecHelpContentWithoutLabel>
            </Form.Item>

            <Row>
              <Col xs={16}>
                <Form.Item
                  name='quantity'
                  style={styles.formItem}
                  rules={[
                    getRequiredRule({
                      fieldName: ['quantity'],
                      displayName: 'Quantity',
                    }),
                  ]}
                >
                  <QaSpecHelpContentWithoutLabel
                    fieldPath={TOOlTIP_FIELDS.quantity.fieldPath}
                    isEnabled={isQaSpecType}
                  >
                    <InputNumber
                      style={styles.inputNumber}
                      min={0}
                      max={9999}
                      placeholder='Quantity'
                    />
                  </QaSpecHelpContentWithoutLabel>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name='quantityUom'
                  style={styles.formItem}
                  rules={[
                    getRequiredRule({
                      fieldName: ['quantityUom'],
                      displayName: 'Unit',
                    }),
                  ]}
                >
                  <QaSpecHelpContentWithoutLabel
                    fieldPath={TOOlTIP_FIELDS.quantityUom.fieldPath}
                    isEnabled={isQaSpecType}
                  >
                    <WrapperSelect
                      placeholder='Unit'
                      getPopupContainer={(triggerNode) => {
                        return triggerNode;
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value='Gram'>g</Option>
                      <Option value='Milligram'>mg</Option>
                      <Option value='Microgram'>mcg</Option>
                    </WrapperSelect>
                  </QaSpecHelpContentWithoutLabel>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name='dailyValuePrecisionCode'
              style={styles.formItem}
              rules={[
                getRequiredRule({
                  fieldName: ['dailyValuePrecisionCode'],
                  displayName: 'Daily Value Precision',
                }),
              ]}
            >
              <QaSpecHelpContentWithoutLabel
                fieldPath={TOOlTIP_FIELDS.dailyValuePrecision.fieldPath}
                isEnabled={isQaSpecType}
              >
                <WrapperSelect
                  placeholder='Select Daily Value Precision'
                  showSearch
                  filterOption={(input, option) =>
                    option?.value
                      ?.toLowerCase()
                      .indexOf(input?.toLowerCase()) >= 0
                  }
                  getPopupContainer={(triggerNode) => {
                    return triggerNode;
                  }}
                >
                  {sortBy(dailyPrecisionCodeEnum?.[0]?.enumProperties, [
                    'enumDisplayName',
                    'enumGroup',
                  ])?.map((item, index) => (
                    <Option value={item?.enumDisplayName} key={index}>
                      {item?.enumDisplayName}
                    </Option>
                  ))}
                </WrapperSelect>
              </QaSpecHelpContentWithoutLabel>
            </Form.Item>
            <Row>
              <Col span={24}>
                <Form.Item
                  name='dailyValueIntakePercent'
                  style={styles.formItem}
                  rules={[
                    getRequiredRule({
                      fieldName: ['dailyValueIntakePercent'],
                      displayName: 'Daily Value',
                    }),
                  ]}
                >
                  <QaSpecHelpContentWithoutLabel
                    fieldPath={TOOlTIP_FIELDS.dailyValueIntakePercent.fieldPath}
                    isEnabled={isQaSpecType}
                    style={{
                      marginTop: 6,
                    }}
                  >
                    <InputNumber
                      placeholder='Daily Value'
                      min={0}
                      style={styles.inputNumber}
                      onKeyPress={handleNumberInput}
                      addonAfter={'%'}
                    />
                  </QaSpecHelpContentWithoutLabel>
                </Form.Item>
              </Col>
            </Row>
            <Row style={styles.rowMargin}>
              <Col xs={12}>
                <Button
                  icon={<StopOutlined />}
                  onClick={onCancel}
                  style={styles.cancelBtn}
                  danger
                >
                  Cancel
                </Button>
              </Col>
              <Col xs={12}>
                {method === 'update' ? (
                  <Button
                    htmlType='submit'
                    style={{ width: '100%', height: '35px' }}
                    type='primary'
                  >
                    Update
                  </Button>
                ) : (
                  <Button
                    ref={refAddBtn}
                    htmlType='submit'
                    style={{ width: '100%', height: '35px' }}
                    type='primary'
                  >
                    Add New
                  </Button>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item name='nutrientIdx' hidden>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </React.Fragment>
      )}
    </div>
  );
};

NutrientForm.propsTypes = {
  method: PropTypes.oneOf(['add', 'update']),
};

export default NutrientForm;
